/**
 * Master Data Cache Service
 * Provides instant access to master data from IndexedDB (renderer process)
 * Falls back to NeDB (main process via IPC) if not cached in Electron mode
 * Falls back to Firestore if not cached in web mode
 * This eliminates IPC bottleneck for frequent reads
 */

import offlineDB from '../indexedDB/offlineDB';
import masterDataService from './masterDataService';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { isDesktopMode, hasElectronAPI } from '../environmentDetector';

class MasterDataCache {
  private cacheStatus: Map<string, boolean> = new Map();
  private loadingPromises: Map<string, Promise<any>> = new Map();

  /**
   * Get a single document from cache
   * Falls back to NeDB (Electron) or Firestore (web) if not in cache
   */
  async get(collectionName: string, docId: string): Promise<any> {
    try {
      // Try IndexedDB first (instant, no IPC)
      const cached = await offlineDB.getMasterData(collectionName, docId);
      if (cached) {
        return cached;
      }

      // Determine fallback based on environment
      const inElectronMode = isDesktopMode();
      const hasAPI = hasElectronAPI();

      if (inElectronMode && hasAPI) {
        // Electron mode - fall back to NeDB via IPC
        console.log(`[MasterDataCache] Cache miss for ${collectionName}/${docId}, fetching from NeDB`);
        const docData = await masterDataService.getMasterData(collectionName, docId);

        // Cache it for next time
        if (docData) {
          await offlineDB.saveMasterData(collectionName, docId, docData);
        }

        return docData;
      } else if (navigator.onLine) {
        // Web mode - fall back to Firestore
        console.log(`[MasterDataCache] Cache miss for ${collectionName}/${docId}, fetching from Firestore (web mode)`);
        const docRef = doc(db, collectionName, docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const docData = { id: docSnap.id, ...docSnap.data() };
          // Cache it for next time
          await offlineDB.saveMasterData(collectionName, docId, docData);
          return docData;
        }
      }

      return null;
    } catch (error) {
      console.error(`[MasterDataCache] Error getting ${collectionName}/${docId}:`, error);
      return null;
    }
  }

  /**
   * Get all documents for a collection from cache
   * Falls back to NeDB (Electron) or Firestore (web) if not cached
   */
  async getAll(collectionName: string): Promise<any[]> {
    try {
      // Check if collection is already cached
      const cacheKey = `all:${collectionName}`;

      // If already loading, wait for that promise
      if (this.loadingPromises.has(cacheKey)) {
        return await this.loadingPromises.get(cacheKey);
      }

      // Try IndexedDB first (instant, no IPC)
      const cached = await offlineDB.getAllMasterData(collectionName);

      if (cached && cached.length > 0) {
        return cached;
      }

      // Determine fallback based on environment
      const inElectronMode = isDesktopMode();
      const hasAPI = hasElectronAPI();

      let loadingPromise: Promise<any[]>;

      if (inElectronMode && hasAPI) {
        // Desktop mode - local DB first, Firestore if that store is still empty
        console.log(`[MasterDataCache] Cache miss for ${collectionName}, fetching from desktop DB`);
        loadingPromise = this.loadFromNeDB(collectionName).then(async (docs) => {
          if ((!docs || docs.length === 0) && navigator.onLine) {
            console.log(`[MasterDataCache] Desktop DB empty for ${collectionName}, fetching from Firestore`);
            return this.loadFromFirestore(collectionName);
          }
          return docs;
        });
      } else if (navigator.onLine) {
        // Web mode - fall back to Firestore
        console.log(`[MasterDataCache] Cache miss for ${collectionName}, fetching from Firestore (web mode)`);
        loadingPromise = this.loadFromFirestore(collectionName);
      } else {
        // Offline and no cache - return empty
        console.warn(`[MasterDataCache] No cache and offline for ${collectionName}`);
        return [];
      }

      // Create loading promise to prevent duplicate requests
      this.loadingPromises.set(cacheKey, loadingPromise);

      try {
        const docs = await loadingPromise;
        this.cacheStatus.set(cacheKey, true);
        return docs;
      } finally {
        this.loadingPromises.delete(cacheKey);
      }
    } catch (error) {
      console.error(`[MasterDataCache] Error getting all ${collectionName}:`, error);
      return [];
    }
  }

  /**
   * Get documents filtered by storeId from cache
   */
  async getByStore(collectionName: string, storeId: string): Promise<any[]> {
    try {
      // Try IndexedDB first
      const cached = await offlineDB.getMasterDataByStore(collectionName, storeId);

      if (cached && cached.length > 0) {
        return cached;
      }

      // Determine fallback based on environment
      const inElectronMode = isDesktopMode();
      const hasAPI = hasElectronAPI();

      let docs: any[] = [];

      if (inElectronMode && hasAPI) {
        console.log(`[MasterDataCache] Cache miss for ${collectionName} (storeId: ${storeId}), fetching from desktop DB`);
        docs = await masterDataService.getAllMasterData(collectionName);
      }

      if ((!docs || docs.length === 0) && navigator.onLine) {
        console.log(`[MasterDataCache] Fetching ${collectionName} (storeId: ${storeId}) from Firestore`);
        const collectionRef = collection(db, collectionName);
        const q = query(collectionRef, where('storeId', '==', storeId));
        const snapshot = await getDocs(q);
        docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      }

      const filtered = docs.filter(docData => docData.storeId === storeId);

      // Cache all docs (not just filtered ones)
      if (docs.length > 0) {
        await offlineDB.bulkSaveMasterData(collectionName, docs);
      }

      return filtered;
    } catch (error) {
      console.error(`[MasterDataCache] Error getting ${collectionName} by store:`, error);
      return [];
    }
  }

  /**
   * Load from NeDB and cache to IndexedDB (Electron mode)
   */
  private async loadFromNeDB(collectionName: string): Promise<any[]> {
    const docs = await masterDataService.getAllMasterData(collectionName);

    // Cache to IndexedDB
    if (docs && docs.length > 0) {
      await offlineDB.bulkSaveMasterData(collectionName, docs);
      console.log(`[MasterDataCache] Cached ${docs.length} ${collectionName} documents to IndexedDB from NeDB`);
    }

    return docs || [];
  }

  /**
   * Load from Firestore and cache to IndexedDB (Web mode)
   */
  private async loadFromFirestore(collectionName: string): Promise<any[]> {
    try {
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      const docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

      // Cache to IndexedDB
      if (docs && docs.length > 0) {
        await offlineDB.bulkSaveMasterData(collectionName, docs);
        console.log(`[MasterDataCache] Cached ${docs.length} ${collectionName} documents to IndexedDB from Firestore`);
      }

      return docs || [];
    } catch (error) {
      console.error(`[MasterDataCache] Error loading ${collectionName} from Firestore:`, error);
      return [];
    }
  }

  /**
   * Save/update a document in cache
   */
  async save(collectionName: string, docId: string, data: any): Promise<void> {
    try {
      await offlineDB.saveMasterData(collectionName, docId, data);
      console.log(`[MasterDataCache] Saved ${collectionName}/${docId} to cache`);
    } catch (error) {
      console.error(`[MasterDataCache] Error saving ${collectionName}/${docId}:`, error);
      throw error;
    }
  }

  /**
   * Bulk save documents to cache
   */
  async bulkSave(collectionName: string, docs: any[]): Promise<void> {
    try {
      await offlineDB.bulkSaveMasterData(collectionName, docs);
      this.cacheStatus.set(`all:${collectionName}`, true);
      console.log(`[MasterDataCache] Bulk saved ${docs.length} ${collectionName} documents to cache`);
    } catch (error) {
      console.error(`[MasterDataCache] Error bulk saving ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document from cache
   */
  async delete(collectionName: string, docId: string): Promise<void> {
    try {
      await offlineDB.deleteMasterData(collectionName, docId);
      console.log(`[MasterDataCache] Deleted ${collectionName}/${docId} from cache`);
    } catch (error) {
      console.error(`[MasterDataCache] Error deleting ${collectionName}/${docId}:`, error);
      throw error;
    }
  }

  /**
   * Clear all documents in a collection from cache
   */
  async clear(collectionName: string): Promise<void> {
    try {
      await offlineDB.clearMasterDataCollection(collectionName);
      this.cacheStatus.delete(`all:${collectionName}`);
      console.log(`[MasterDataCache] Cleared ${collectionName} cache`);
    } catch (error) {
      console.error(`[MasterDataCache] Error clearing ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Preload priority collections first (critical data needed for app to function)
   * This should be called before preloadAll for faster initial app load
   */
  async preloadPriority(storeId?: string): Promise<void> {
    const inElectronMode = isDesktopMode();
    const hasAPI = hasElectronAPI();

    console.log(`[MasterDataCache] Preloading priority master data (${inElectronMode ? 'Electron' : 'Web'} mode)...`);

    // Critical collections needed for app to function
    const priorityCollections = [
      'stores',
      'paymentMethods',
      'taxes',
      'items',
      'categories',
    ];

    const startTime = Date.now();
    let totalDocs = 0;

    for (const collectionName of priorityCollections) {
      try {
        let docs: any[] = [];

        // Load from appropriate source based on environment
        if (inElectronMode && hasAPI) {
          docs = await masterDataService.getAllMasterData(collectionName);
        } else if (navigator.onLine) {
          try {
            const collectionRef = collection(db, collectionName);
            const snapshot = await getDocs(collectionRef);
            docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          } catch (error) {
            console.warn(`[MasterDataCache] Failed to load ${collectionName} from Firestore:`, error);
          }
        }

        if (docs && docs.length > 0) {
          let filteredDocs = docs;
          if (storeId) {
            if (collectionName === 'stores') {
              filteredDocs = docs.filter(doc => (doc.id || doc._id) === storeId);
            } else {
              filteredDocs = docs.filter(doc => doc.storeId === storeId);
            }
          }

          if (collectionName === 'stores' && storeId) {
            await offlineDB.clearMasterDataCollection('stores');
          }

          if (filteredDocs.length > 0) {
            await offlineDB.bulkSaveMasterData(collectionName, filteredDocs);
            totalDocs += filteredDocs.length;
            this.cacheStatus.set(`all:${collectionName}`, true);
          }
        }
      } catch (error) {
        console.warn(`[MasterDataCache] Failed to preload priority ${collectionName}:`, error);
      }
    }

    const duration = Date.now() - startTime;
    console.log(`[MasterDataCache] Preloaded ${totalDocs} priority documents in ${duration}ms`);
  }

  /**
   * Preload all master data collections from NeDB (Electron) or Firestore (web) to IndexedDB
   * This should be called on app startup
   */
  async preloadAll(storeId?: string): Promise<void> {
    const inElectronMode = isDesktopMode();
    const hasAPI = hasElectronAPI();

    console.log(`[MasterDataCache] Preloading master data to IndexedDB (${inElectronMode ? 'Electron' : 'Web'} mode)...`);

    // Only preload collections that have NeDB repos in Electron storage
    const collections = [
      'stores',
      'items',
      'menus',
      'categories',
      'taxes',
      'customFields',
      'advancedOption', // Advanced option sets
      'topMenus',
      'customers',
      'tags',
      'tables',
      'serviceFees',
      'feesDiscounts',
      'paymentMethods',
      'openingHours',
      'images',
    ];

    const startTime = Date.now();
    let totalDocs = 0;

    for (const collectionName of collections) {
      try {
        let docs: any[] = [];

        // Load from appropriate source based on environment
        if (inElectronMode && hasAPI) {
          // Electron mode - load from NeDB
          docs = await masterDataService.getAllMasterData(collectionName);
        } else if (navigator.onLine) {
          // Web mode - load from Firestore
          try {
            const collectionRef = collection(db, collectionName);
            const snapshot = await getDocs(collectionRef);
            docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          } catch (error) {
            console.warn(`[MasterDataCache] Failed to load ${collectionName} from Firestore:`, error);
          }
        }

        if (docs && docs.length > 0) {
          // Filter by storeId if provided
          let filteredDocs = docs;
          if (storeId) {
            if (collectionName === 'stores') {
              filteredDocs = docs.filter(doc => (doc.id || doc._id) === storeId);
            } else {
              filteredDocs = docs.filter(doc => doc.storeId === storeId);
            }
          }

          // If filtering stores, clear existing stores to avoid stale entries
          if (collectionName === 'stores' && storeId) {
            await offlineDB.clearMasterDataCollection('stores');
          }

          // Cache to IndexedDB
          if (filteredDocs.length > 0) {
            await offlineDB.bulkSaveMasterData(collectionName, filteredDocs);
            totalDocs += filteredDocs.length;
            this.cacheStatus.set(`all:${collectionName}`, true);
          }
        }
      } catch (error) {
        console.warn(`[MasterDataCache] Failed to preload ${collectionName}:`, error);
      }
    }

    const duration = Date.now() - startTime;
    console.log(`[MasterDataCache] Preloaded ${totalDocs} documents in ${duration}ms`);
  }

  /**
   * Check if a collection is cached
   */
  isCached(collectionName: string): boolean {
    return this.cacheStatus.get(`all:${collectionName}`) === true;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { collection: string; cached: boolean }[] {
    const stats: { collection: string; cached: boolean }[] = [];
    this.cacheStatus.forEach((cached, key) => {
      stats.push({ collection: key, cached });
    });
    return stats;
  }
}

// Singleton instance
const masterDataCache = new MasterDataCache();

export default masterDataCache;